import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/LanguageContext";
import { useTexts } from "@/TextsContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_HERO_DESKTOP = "/images/hero-default.webp";
const DEFAULT_HERO_MOBILE = "/images/hero-default-mobile.webp";

export default function HeroSection() {
  const { lang } = useLang();
  const tx = t[lang];
  const { texts } = useTexts();
  const hero = texts?.[lang]?.hero || {};
  const heroTag = hero.tag || tx.hero_tag;
  const heroH1_1 = hero.h1_1 || tx.hero_h1_1;
  const heroH1_2 = hero.h1_2 || tx.hero_h1_2;
  const heroSub = hero.sub || tx.hero_sub;
  const [heroUrl, setHeroUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/media/slot/hero/random`)
      .then(({ data }) => {
        if (data?.url && data?.has_custom) {
          setHeroUrl(data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        }
      })
      .catch(() => {});
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector("#kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex items-center pt-14 sm:pt-16 overflow-hidden">
      <div className="absolute inset-0 z-0 overflow-hidden">
        {heroUrl ? (
          <img src={heroUrl} alt="Goldbarren" className="w-full h-full object-cover animate-ken-burns" data-testid="hero-background-image" fetchpriority="high" decoding="async" />
        ) : (
          <picture>
            <source srcSet={DEFAULT_HERO_MOBILE} media="(max-width: 767px)" type="image/webp" />
            <source srcSet={DEFAULT_HERO_DESKTOP} media="(min-width: 768px)" type="image/webp" />
            <img src={DEFAULT_HERO_DESKTOP} alt="Goldbarren" className="w-full h-full object-cover animate-ken-burns" data-testid="hero-background-image" fetchpriority="high" decoding="async" />
          </picture>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C1620]/95 via-[#0C1620]/85 to-[#0C1620]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C1620] via-transparent to-[#0C1620]/40" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative z-10 w-full">
        <div className="max-w-3xl">
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] text-[#C5993A] mb-4 sm:mb-6 animate-fade-in-up font-medium">
            {heroTag}
          </p>
          <h1 data-testid="hero-headline" className="font-heading text-3xl sm:text-5xl lg:text-7xl tracking-tight font-light text-white leading-[1.15] sm:leading-[1.1] mb-5 sm:mb-8 animate-fade-in-up animation-delay-200">
            {heroH1_1}{" "}
            <span className="block text-[#C5993A] text-3xl sm:text-4xl lg:text-6xl whitespace-nowrap">{heroH1_2}</span>
          </h1>
          <p data-testid="hero-subheadline" className="text-sm sm:text-base lg:text-lg text-[#F0F4F8]/80 leading-relaxed max-w-2xl mb-8 sm:mb-12 animate-fade-in-up animation-delay-400 font-light">
            {heroSub}
          </p>
          <div className="animate-fade-in-up animation-delay-600">
            <Button data-testid="hero-cta-button" onClick={scrollToContact} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium px-5 sm:px-8 py-5 sm:py-6 text-sm sm:text-base rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0 group w-full sm:w-auto">
              {tx.hero_cta}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          <div className="mt-10 sm:mt-16 animate-fade-in-up animation-delay-800">
            <p className="text-[#C5993A]/70 italic font-bold font-heading text-base sm:text-lg" style={{ fontWeight: 700 }}>
              {tx.hero_quote}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <span className="block h-px w-10 bg-gradient-to-r from-[#C5993A]/70 to-transparent" />
              <span className="text-[#C5993A]/60 text-xs uppercase tracking-[0.2em] font-light">
                {tx.hero_quote_source}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C5993A]/40 to-transparent" />
    </section>
  );
}
